"use client";

import { useCallback, useEffect, useRef, RefObject } from "react";
import { useTranslations } from "next-intl";
import { isChatUnavailableError, useStateHistoryChatPage } from "@/app/store/chat";
import { ScrollButton } from "@/app/components/ui";
import ChatMessageItem from "./ChatMessageItem";
import { Skeleton } from "@/app/components/ui/skeleton";
import { useToast, useAuth } from "@/app/hooks";
import { cn } from "@/app/lib/utils";
import { MAX_MESSAGES_PER_TOPIC } from "@/app/lib/constants";
import {
   useChatHistoryLoader,
   sortMessages,
   loadAllPages
} from "../hooks/useChatHistoryLoader";
import { useChatStreaming } from "../hooks/useChatStreaming";
import { useHistoryViewGuard } from "../hooks/chatStreaming/useHistoryViewGuard";
import LoadingMessage from "./LoadingMessage";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { useAiSourceOptions } from "@/app/components/ui/AiSourceSelector";

type ChatMessageProps = {
   inputRefFocusAfterFinishChat?: RefObject<HTMLTextAreaElement | null>;
   shareKey?: string;
   className?: string;
   studentChatHistoryId?: string;
   onShareComplete?: () => void;
   onChatUnavailable?: (isUnavailable: boolean) => void;
};

export const ChatMessages = (props: ChatMessageProps) => {
   const {
      inputRefFocusAfterFinishChat,
      shareKey,
      className,
      studentChatHistoryId,
      onChatUnavailable
   } = props;
   const translation = useTranslations("Home");
   const translateChat = useTranslations("Chat");
   const { toast } = useToast();

   const {
      historyItem,
      setHistoryItem,
      loadMessages,
      loadMessagesShared,
      lastSendMessageEvent,
      loadingMoreMessageChat: loadingMore,
      setLoadingMoreMessageChat: setLoadingMore,
      hasActiveGeneration,
      isTemporaryChat,
      errorChat,
      messagePageInfo
   } = useStateHistoryChatPage();

   const messageListRef = useRef<HTMLDivElement>(null);
   const messagesEndRef = useRef<HTMLDivElement>(null);
   const wrapMessageHeightRef = useRef<HTMLDivElement>(null);
   const shareJustCompletedRef = useRef(false);

   // Guards shared chat state against out-of-order writes when switching chats
   // mid-stream. See useHistoryViewGuard.
   const viewGuard = useHistoryViewGuard();
   // Set when a brand-new chat is assigned its server id mid-stream, so the
   // history loader can distinguish an id assignment from a real chat switch.
   const newChatIdAssignedRef = useRef<string | null>(null);
   // Tracks the previously loaded chat id to detect genuine chat switches.
   const prevActiveHistoryIdRef = useRef<string>("");

   const activeHistoryId = studentChatHistoryId || historyItem?.id || "";

   // 1. Hook quản lý dữ liệu lịch sử hội thoại & phân trang
   const {
      history,
      setHistory,
      ownerShareId,
      setOwnerShareId,
      handleCreateNewChat,
      handleAddToHistory
   } = useChatHistoryLoader({ shareKey });

   const markChatUnavailable = useCallback(() => {
      onChatUnavailable?.(true);
      setHistory([]);
      setHistoryItem(null);
      useStateHistoryChatPage.setState({
         messagePageInfo: undefined,
         sharedKnowledgeBaseId: null
      });
   }, [onChatUnavailable, setHistory, setHistoryItem]);

   // 2. Hook quản lý luồng SSE streaming & khôi phục kết nối (reconnect)
   const {
      streamingResponse,
      loading,
      chat,
      clearChatStates,
      isReconnectingRef,
      isCreatingNewChatRef
   } = useChatStreaming({
      history,
      setHistory,
      ownerShareId,
      shareKey,
      studentChatHistoryId,
      activeHistoryId,
      handleCreateNewChat,
      handleAddToHistory,
      messageListRef,
      messagesEndRef,
      wrapMessageHeightRef,
      inputRefFocusAfterFinishChat,
      viewGuard,
      newChatIdAssignedRef
   });

   const { setSelectedBaseKnowledge } = useAiSourceSelector();
   const aiSourceOptions = useAiSourceOptions();

   /**
    * Sync the AI source selector to match the shared chat's knowledgeBase.
    * Only auto-switches if user has access to the project.
    */
   const syncSharedKnowledgeBase = useCallback(
      (knowledgeBaseId: string) => {
         const currentKnowledgeBaseId =
            useAiSourceSelector.getState().selectedBaseKnowledge.id;
         if (currentKnowledgeBaseId === knowledgeBaseId) return;

         const option = aiSourceOptions.find((opt) => opt.id === knowledgeBaseId);
         if (!option) return;

         if (!option.isPremium) {
            setSelectedBaseKnowledge(option);
         }
      },
      [setSelectedBaseKnowledge, aiSourceOptions]
   );

   const resetWrapMessageHeight = useCallback(() => {
      if (wrapMessageHeightRef.current) {
         wrapMessageHeightRef.current.style.height = "auto";
      }
   }, []);

   const scrollToLatestMessage = useCallback(() => {
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
         });
      });
   }, []);

   // Reset chiều cao khung chat về auto khi kết thúc streaming
   useEffect(() => {
      if (!loading) {
         resetWrapMessageHeight();
      }
   }, [loading, resetWrapMessageHeight]);

   // Lắng nghe sự kiện khi người dùng nhấn gửi tin nhắn
   useEffect(() => {
      if (lastSendMessageEvent?.text) {
         resetWrapMessageHeight();
         const currentHeight = wrapMessageHeightRef.current?.clientHeight || 0;
         const screenChatHeight = messageListRef.current?.clientHeight || 500;
         if (wrapMessageHeightRef.current) {
            wrapMessageHeightRef.current.style.height = `${
               currentHeight + screenChatHeight
            }px`;
         }
         messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
         const textToSend = lastSendMessageEvent.text;
         lastSendMessageEvent.text = "";
         lastSendMessageEvent.key = null;
         chat(textToSend);
      }
   }, [lastSendMessageEvent, chat, resetWrapMessageHeight]);

   // Tải dữ liệu tin nhắn đầu tiên khi mount hoặc chuyển đổi hội thoại
   useEffect(() => {
      const historyId = activeHistoryId;

      // Distinguish a genuine chat switch from a new chat receiving its server
      // id mid-stream (activeHistoryId changes from "" to the fresh id).
      const prevHistoryId = prevActiveHistoryIdRef.current;
      const isNewChatIdAssignment =
         !!historyId && newChatIdAssignedRef.current === historyId;
      const isRealSwitch = !isNewChatIdAssignment && prevHistoryId !== historyId;
      prevActiveHistoryIdRef.current = historyId;
      if (isNewChatIdAssignment) {
         newChatIdAssignedRef.current = null;
      }

      if (isCreatingNewChatRef.current) return;

      if (shareJustCompletedRef.current) {
         shareJustCompletedRef.current = false;
         return;
      }

      // A reconnect to the SAME chat must not reload/reset it. But a genuine
      // switch to a different chat must proceed even while a reconnect (e.g. for
      // a just-created chat) is in flight — otherwise the new chat never loads
      // and the previous chat's stream keeps writing into the view.
      if (isReconnectingRef.current && !isRealSwitch) return;

      // On a real switch, advance the view and drop stale content immediately so
      // the previous chat's in-flight messages never linger while the new chat
      // loads. Advancing the view also invalidates any late writes from the
      // previous chat's submission/reconnect flows.
      if (isRealSwitch) {
         viewGuard.advanceView();
         setHistory([]);
      }
      const scope = viewGuard.capture();

      clearChatStates();
      setLoadingMore(false);
      resetWrapMessageHeight();

      const abortController = new AbortController();
      const currentHistoryId = historyId;

      if (shareKey) {
         onChatUnavailable?.(false);
         loadAllPages((page) =>
            loadMessagesShared(shareKey, page, MAX_MESSAGES_PER_TOPIC)
         )
            .then((res) => {
               if (abortController.signal.aborted) return;
               if (res?.messages.items?.length) {
                  const sortedMessages = sortMessages(res);
                  setOwnerShareId(res?.userId);
                  scope.run(() => setHistory(sortedMessages));
                  useStateHistoryChatPage.setState({
                     messagePageInfo: res.messages.pageInfo
                  });
                  scrollToLatestMessage();

                  // Auto-switch knowledge base to match shared chat's project
                  const sharedKnowledgeBase = res.messages.items.find(
                     (msg: any) => msg.knowledgeBase?.length
                  )?.knowledgeBase?.[0];
                  if (sharedKnowledgeBase) {
                     useStateHistoryChatPage
                        .getState()
                        .setSharedKnowledgeBaseId(sharedKnowledgeBase);
                     syncSharedKnowledgeBase(sharedKnowledgeBase);
                  }
               } else {
                  scope.run(() => setHistory([]));
                  useStateHistoryChatPage.setState({ messagePageInfo: undefined });
               }
            })
            .catch((error) => {
               if (abortController.signal.aborted) return;
               if (isChatUnavailableError(error)) {
                  markChatUnavailable();
                  return;
               }

               console.error("Error loading shared messages:", error);
               toast({
                  variant: "destructive",
                  description: translation("failedToLoadMessages")
               });
               setHistoryItem(null);
            });

         return () => {
            abortController.abort();
         };
      }

      if (historyId) {
         onChatUnavailable?.(false);
         const loadInitialMessages = async () => {
            try {
               const currentHistoryIdLoad = studentChatHistoryId || historyItem?.id || "";
               setLoadingMore(true);
               const data = await loadAllPages((page) =>
                  loadMessages(currentHistoryIdLoad, page, MAX_MESSAGES_PER_TOPIC)
               );

               if (abortController.signal.aborted) return;

               const isUpdateNewDataHistoryItem = data && data.id;
               if (isUpdateNewDataHistoryItem) {
                  const latestHistoryId =
                     useStateHistoryChatPage.getState().historyItem?.id;
                  if (latestHistoryId === currentHistoryId || !latestHistoryId) {
                     setHistoryItem(data as any);
                  }
               }
               if (data && data?.messages?.items?.length) {
                  const sortedMessages = sortMessages(data);
                  setOwnerShareId(data?.userId);
                  scope.run(() => setHistory(sortedMessages));
                  useStateHistoryChatPage.setState({
                     messagePageInfo: data.messages.pageInfo
                  });
                  scrollToLatestMessage();
               }
            } catch (error) {
               if (abortController.signal.aborted) return;
               if (isChatUnavailableError(error)) {
                  markChatUnavailable();
                  return;
               }

               console.error("Error loading messages:", error);
               toast({
                  variant: "destructive",
                  description: translation("failedToLoadMessages")
               });
               setHistoryItem(null);
            } finally {
               if (!abortController.signal.aborted) {
                  setLoadingMore(false);
               }
            }
         };
         loadInitialMessages();
      } else {
         scope.run(() => setHistory([]));
         useStateHistoryChatPage.setState({ messagePageInfo: undefined });
      }

      return () => {
         abortController.abort();
      };
   }, [
      activeHistoryId,
      shareKey,
      clearChatStates,
      loadMessagesShared,
      loadMessages,
      setHistory,
      setHistoryItem,
      setLoadingMore,
      setOwnerShareId,
      scrollToLatestMessage,
      resetWrapMessageHeight,
      onChatUnavailable,
      markChatUnavailable,
      syncSharedKnowledgeBase,
      translation,
      toast,
      viewGuard,
      studentChatHistoryId,
      historyItem?.id
   ]);

   const isGenerating =
      loading || Boolean(activeHistoryId && hasActiveGeneration(activeHistoryId));
   const isBanned = (() => {
      try {
         const parsed = errorChat ? JSON.parse(errorChat) : null;
         return parsed?.code === "ACCOUNT_BANNED";
      } catch {
         return false;
      }
   })();
   const isTopicFull = (messagePageInfo?.total || 0) >= MAX_MESSAGES_PER_TOPIC;
   const isLastMessageFromUser = history[history.length - 1]?.role === "user";
   const canResendLastMessage =
      isLastMessageFromUser &&
      !isGenerating &&
      !shareKey &&
      !isBanned &&
      !isTopicFull;

   return (
      <div
         className={cn(
            "flex-1 sm:p-1 overflow-y-auto overflow-x-hidden mt-12 relative",
            className
         )}
         ref={messageListRef}
      >
         <div
            ref={wrapMessageHeightRef}
            className={cn(
               "max-w-full sm:max-w-3xl mx-auto space-y-1.5 sm:space-y-4 relative"
            )}
         >
            {history.map((msg, index) => (
               <div key={msg.id || `pending-${index}`} data-role={msg.role}>
                  <ChatMessageItem
                     msg={msg}
                     shareKey={shareKey}
                     isShowNote={index % 2 > 0 && index >= history.length - 1}
                     canResend={canResendLastMessage && index === history.length - 1}
                  />
               </div>
            ))}
            {history.length === 0 && loadingMore && (
               <div className="space-y-6 opacity-60 px-2 sm:px-0">
                  <div className="flex justify-end">
                     <Skeleton className="h-[52px] w-[60%] sm:w-[40%] rounded-2xl" />
                  </div>
                  <div className="flex justify-start">
                     <Skeleton className="h-[90px] w-[80%] sm:w-[70%] rounded-2xl" />
                  </div>
               </div>
            )}
            {((loading && (!activeHistoryId || isTemporaryChat)) ||
               (activeHistoryId && hasActiveGeneration(activeHistoryId))) && (
               <LoadingMessage
                  streamingResponse={streamingResponse}
                  translate={translateChat}
               />
            )}
            <div ref={messagesEndRef} />

            {history.length > 2 && (
               <div>
                  <ScrollButton containerRef={messageListRef} />
               </div>
            )}
         </div>
      </div>
   );
};

export default ChatMessages;
